import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Tabs,
  Tab,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Pagination,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Swal from 'sweetalert2';
import { getRequest, patchRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

const stageTheme = {
  boardBg: '#eef6fb',
  boardBorder: '#9ec3d6',
  divider: '#b9d3e1',
  columnHeader: '#234a5f',
  cardBg: '#ffffff',
  cardBorder: '#dbeafe',
};

const priorityTheme = {
  low: { bg: '#58c7cf', color: '#0f3f5b' },
  medium: { bg: '#68de58', color: '#0b3f2a' },
  high: { bg: '#ff7f9c', color: '#253143' },
  critical: { bg: '#ff6e8e', color: '#253143' },
};

function TaskKanban(props) {
  const [tasks, setTasks] = React.useState([]);
  const [tab, setTab] = React.useState('board');
  const [loading, setLoading] = React.useState(false);
  const [draggingTaskId, setDraggingTaskId] = React.useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [menuTask, setMenuTask] = React.useState(null);
  const [detailsDialog, setDetailsDialog] = React.useState({ open: false, loading: false, task: null });
  const [newComment, setNewComment] = React.useState('');
  const [newSubtask, setNewSubtask] = React.useState('');
  const [timelineFrom, setTimelineFrom] = React.useState('');
  const [timelineTo, setTimelineTo] = React.useState('');
  const [selectedSprint, setSelectedSprint] = React.useState('');
  const [workflowTypes, setWorkflowTypes] = React.useState([]);
  const [selectedWorkflowTypeId, setSelectedWorkflowTypeId] = React.useState('');
  const [workflowStages, setWorkflowStages] = React.useState([]);
  const [hoverWorkflowStageId, setHoverWorkflowStageId] = React.useState('');
  const currentUserId = React.useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.id != null ? Number(user.id) : null;
    } catch {
      return null;
    }
  }, []);
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 50 });
  const [rowCount, setRowCount] = React.useState(0);
  const [sprints, setSprints] = React.useState([]);

  const loadTasks = React.useCallback(() => {
    if (!currentUserId) {
      setTasks([]);
      setRowCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = {
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
      assigned_to: currentUserId,
      ...(selectedSprint ? { sprint: selectedSprint } : {}),
      ...(selectedWorkflowTypeId ? { task_type: selectedWorkflowTypeId } : {})
    };
    getRequest(GET_URL.tasks.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const rows = (response.data?.data || []).filter(
            (task) => Number(task.assigned_to) === Number(currentUserId)
          );
          setTasks(rows);
          setRowCount(response.data?.count || 0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [props, paginationModel.page, paginationModel.pageSize, currentUserId, selectedSprint, selectedWorkflowTypeId]);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  React.useEffect(() => {
    getRequest(GET_URL.sprints.api, { limit: 200, pageno: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        setSprints(response.data?.data || []);
      }
    });
  }, [props]);

  React.useEffect(() => {
    getRequest(GET_URL.task_types.api, { limit: 200, pageno: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        setWorkflowTypes(response.data?.data || []);
      }
    });
  }, [props]);

  React.useEffect(() => {
    if (!selectedWorkflowTypeId) {
      setWorkflowStages([]);
      return;
    }
    getRequest(
      GET_URL.task_type_stages.api,
      { limit: 500, pageno: 1, task_type: selectedWorkflowTypeId },
      props
    ).then((response) => {
      if (response && response.status === 200) {
        setWorkflowStages(response.data?.data || []);
      }
    });
  }, [selectedWorkflowTypeId, props]);

  const kanbanTasks = tasks || [];

  const sprintOptions = React.useMemo(() => {
    return sprints.map((s) => ({ id: String(s.id), name: s.name || `Sprint ${s.id}` }));
  }, [sprints]);

  const workflowStagesSorted = React.useMemo(
    () => [...workflowStages].sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)),
    [workflowStages]
  );

  const workflowBoard = React.useMemo(() => {
    const grouped = {};
    workflowStagesSorted.forEach((col) => {
      grouped[String(col.id)] = [];
    });
    (kanbanTasks || []).forEach((task) => {
      const sid = task.task_stage != null ? String(task.task_stage) : '';
      if (sid && grouped[sid]) {
        grouped[sid].push(task);
      }
    });
    return grouped;
  }, [kanbanTasks, workflowStagesSorted]);

  const renderTimelineLineChart = () => {
    if (!selectedWorkflowTypeId || !workflowStagesSorted.length) {
      return (
        <Alert severity="info" sx={{ mb: 1 }}>
          Select a task type above to plot the timeline using that workflow&apos;s stages.
        </Alert>
      );
    }

    const rangeStart = timelineFrom ? new Date(`${timelineFrom}T00:00:00`).getTime() : null;
    const rangeEnd = timelineTo ? new Date(`${timelineTo}T23:59:59.999`).getTime() : null;

    const stageConfig = {};
    workflowStagesSorted.forEach((s, idx) => {
      const raw = String(s.code || '').trim().toLowerCase();
      const k = raw || `stage_${s.id ?? idx}`;
      stageConfig[k] = { key: k, label: s.name || k, value: idx + 1 };
    });
    const nStages = workflowStagesSorted.length;
    const stageTicks = workflowStagesSorted.map((_, idx) => idx + 1);

    const taskColors = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#64748b'];

    const toTime = (value, endOfDay = false) => {
      if (!value) return null;
      const raw = String(value);
      const date = raw.length === 10
        ? new Date(`${raw}T${endOfDay ? '23:59:59.999' : '00:00:00'}`)
        : new Date(raw);
      const ms = date.getTime();
      return Number.isNaN(ms) ? null : ms;
    };

    const normalizeStage = (stageCode) => {
      const key = String(stageCode || '').toLowerCase();
      return stageConfig[key] ? key : null;
    };

    const chartTasks = (kanbanTasks || [])
      .filter((task) => task?.task_stage_code || task?.task_stage)
      .map((task) => {
        const history = Array.isArray(task?.details?.stage_history) ? task.details.stage_history : [];
        const events = history
          .map((event) => ({
            at: toTime(event?.changed_at),
            fromStage: event?.from_stage,
            toStage: event?.to_stage,
          }))
          .filter((event) => event.at && normalizeStage(event.toStage))
          .sort((a, b) => a.at - b.at);
        const firstHistoryTime = events[0]?.at || null;
        const startAt = toTime(task?.created) || toTime(task?.planned_date) || firstHistoryTime || toTime(task?.deadline);
        const initialStage = normalizeStage(
          events[0]?.fromStage || task?.task_stage_code,
        );
        return {
          task,
          events,
          startAt,
          initialStage,
          dataKey: `task_${task.id}`,
        };
      })
      .filter((row) => row.startAt && row.initialStage);

    const eventTimes = new Set();
    chartTasks.forEach((row) => {
      eventTimes.add(row.startAt);
      row.events.forEach((event) => eventTimes.add(event.at));
    });
    if (rangeStart !== null) eventTimes.add(rangeStart);
    if (rangeEnd !== null) eventTimes.add(rangeEnd);
    if (rangeEnd === null) eventTimes.add(Date.now());

    const timelineTimes = [...eventTimes]
      .filter((time) => {
        if (rangeStart !== null && time < rangeStart) return false;
        if (rangeEnd !== null && time > rangeEnd) return false;
        return true;
      })
      .sort((a, b) => a - b);

    if (!timelineTimes.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No task history for the selected date range.
        </Typography>
      );
    }

    const stageAtTime = (row, time) => {
      if (time < row.startAt) return null;
      let stage = row.initialStage;
      row.events.forEach((event) => {
        if (event.at <= time) {
          stage = normalizeStage(event.toStage) || stage;
        }
      });
      return stage;
    };

    const chartData = timelineTimes.map((time) => {
      const row = {
        time,
        date: new Date(time).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      };
      chartTasks.forEach((taskRow) => {
        const stage = stageAtTime(taskRow, time);
        row[taskRow.dataKey] = stage ? stageConfig[stage]?.value : null;
      });
      return row;
    });

    const stageValueToLabel = Object.values(stageConfig).reduce((acc, stage) => {
      acc[stage.value] = stage.label;
      return acc;
    }, {});

    const CustomTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const dateLabel = new Date(label).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      return (
        <Paper sx={{ p: 1.2, border: '1px solid #e5e7eb' }}>
          <Typography variant="caption" fontWeight={700}>{dateLabel}</Typography>
          {payload.filter((entry) => entry.value !== null && entry.value !== undefined).map((entry) => (
            <Typography key={entry.dataKey} variant="caption" display="block" sx={{ color: entry.color }}>
              {entry.name}: {stageValueToLabel[entry.value] || entry.value}
            </Typography>
          ))}
        </Paper>
      );
    };

    return (
      <Box sx={{ width: '100%', height: 340 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 12, right: 24, left: 0, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            />
            <YAxis
              allowDecimals={false}
              domain={[1, Math.max(1, nStages)]}
              ticks={stageTicks}
              tickFormatter={(value) => stageValueToLabel[value] || value}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {chartTasks.map((taskRow, index) => (
              <Line
                key={taskRow.dataKey}
                type="stepAfter"
                dataKey={taskRow.dataKey}
                name={taskRow.task.title || `Task #${taskRow.task.id}`}
                stroke={taskColors[index % taskColors.length]}
                strokeWidth={2.5}
                connectNulls={false}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const getStickyTransform = (index) => {
    const patterns = [
      { rotate: -2, x: 0, y: 0 },
      { rotate: 2, x: 8, y: -6 },
      { rotate: -1, x: -6, y: -4 },
      { rotate: 1, x: 10, y: -8 },
      { rotate: -2, x: -8, y: -6 },
    ];
    const rowShift = Math.floor(index / patterns.length) * 2;
    const p = patterns[index % patterns.length];
    return `translate(${p.x}px, ${p.y + rowShift}px) rotate(${p.rotate}deg)`;
  };

  const onTaskDragStart = (event, taskId) => {
    setDraggingTaskId(taskId);
    event.dataTransfer.setData('text/plain', String(taskId));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onWorkflowStageDrop = (event, stageId) => {
    event.preventDefault();
    const droppedTaskId = Number(event.dataTransfer.getData('text/plain') || draggingTaskId);
    setHoverWorkflowStageId('');
    setDraggingTaskId(null);
    if (!droppedTaskId || stageId == null) return;

    const droppedTask = (tasks || []).find((row) => row.id === droppedTaskId);
    if (!droppedTask || String(droppedTask.task_stage) === String(stageId)) return;

    const prevStage = droppedTask.task_stage;
    setTasks((prev) =>
      prev.map((row) => (row.id === droppedTaskId ? { ...row, task_stage: stageId } : row))
    );

    patchRequest(`${GET_URL.tasks.api}${droppedTaskId}/`, { task_stage: stageId }, props).catch(() => {
      setTasks((prev) =>
        prev.map((row) => (row.id === droppedTaskId ? { ...row, task_stage: prevStage } : row))
      );
      Swal.fire({ icon: 'error', title: 'Unable to update task stage' });
    });
  };

  const openTaskMenu = (event, task) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuTask(task);
  };

  const closeTaskMenu = () => {
    setMenuAnchorEl(null);
    setMenuTask(null);
  };

  const openTaskDetails = () => {
    if (!menuTask) return;
    closeTaskMenu();
    setDetailsDialog({ open: true, loading: true, task: null });
    getRequest(`${GET_URL.tasks.api}${menuTask.id}/`, {}, props)
      .then((response) => {
        if (response && response.status === 200) {
          setDetailsDialog({ open: true, loading: false, task: response.data?.data || null });
        } else {
          setDetailsDialog({ open: true, loading: false, task: menuTask });
        }
      })
      .catch(() => setDetailsDialog({ open: true, loading: false, task: menuTask }));
  };

  const closeTaskDetails = () => {
    setDetailsDialog({ open: false, loading: false, task: null });
    setNewComment('');
    setNewSubtask('');
  };

  const addComment = () => {
    const task = detailsDialog.task;
    if (!task || !newComment.trim()) return;
    const details = task.details || {};
    const comments = Array.isArray(details.comments) ? details.comments : [];
    const nextDetails = {
      ...details,
      comments: [
        ...comments,
        {
          text: newComment.trim(),
          created_at: new Date().toISOString(),
        },
      ],
    };
    patchRequest(`${GET_URL.tasks.api}${task.id}/`, { details: nextDetails }, props).then(() => {
      setDetailsDialog((prev) => ({ ...prev, task: { ...prev.task, details: nextDetails } }));
      setNewComment('');
      loadTasks();
    });
  };

  const addSubtask = () => {
    const task = detailsDialog.task;
    if (!task || !newSubtask.trim()) return;
    const details = task.details || {};
    const subtasks = Array.isArray(details.subtasks) ? details.subtasks : [];
    const nextDetails = {
      ...details,
      subtasks: [
        ...subtasks,
        {
          title: newSubtask.trim(),
          done: false,
          created_at: new Date().toISOString(),
        },
      ],
    };
    patchRequest(`${GET_URL.tasks.api}${task.id}/`, { details: nextDetails }, props).then(() => {
      setDetailsDialog((prev) => ({ ...prev, task: { ...prev.task, details: nextDetails } }));
      setNewSubtask('');
      loadTasks();
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>My Tasks</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tasks assigned to you. Drag cards to update stage.
      </Typography>
      <Paper sx={{ mb: 2, borderRadius: 2, p: 2 }}>
        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="kanban-sprint-label">Sprint</InputLabel>
              <Select
                labelId="kanban-sprint-label"
                label="Sprint"
                value={selectedSprint}
                onChange={(e) => {
                  setSelectedSprint(e.target.value);
                  setPaginationModel(p => ({ ...p, page: 0 }));
                }}
              >
                <MenuItem value="">All sprints</MenuItem>
                {sprintOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="kanban-tasktype-label">Task type</InputLabel>
              <Select
                labelId="kanban-tasktype-label"
                label="Task type"
                value={selectedWorkflowTypeId}
                onChange={(e) => {
                  setSelectedWorkflowTypeId(e.target.value);
                  setPaginationModel(p => ({ ...p, page: 0 }));
                }}
              >
                <MenuItem value="">All types</MenuItem>
                {workflowTypes.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Tabs value={tab} onChange={(_, nextValue) => setTab(nextValue)} variant="scrollable" scrollButtons="auto">
          <Tab value="board" label="Kanban Board" />
          <Tab value="timeline" label="Timeline" />
          {/* <Tab value="list" label="List" /> */}
        </Tabs>
      </Paper>

      {tab === 'board' && selectedWorkflowTypeId && workflowStagesSorted.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This task type has no stages yet. Add stages under Task types & stages, or clear the task type filter to use the legacy board.
        </Alert>
      )}

      {tab === 'board' && selectedWorkflowTypeId && workflowStagesSorted.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 0,
            overflowX: 'auto',
            bgcolor: stageTheme.boardBg,
            border: '1px solid',
            borderColor: stageTheme.boardBorder,
            borderRadius: 4,
            p: 2,
          }}
        >
          {workflowStagesSorted.map((column, idx) => (
            <Box
              key={column.id}
              onDragOver={(event) => {
                event.preventDefault();
                setHoverWorkflowStageId(String(column.id));
              }}
              onDragLeave={() => setHoverWorkflowStageId('')}
              onDrop={(event) => onWorkflowStageDrop(event, column.id)}
              sx={{
                flex: '1 1 240px',
                minWidth: 240,
                maxWidth: 400,
                borderRight:
                  idx < workflowStagesSorted.length - 1 ? `1px solid ${stageTheme.divider}` : 'none',
                px: 2,
                py: 1,
                minHeight: 470,
                bgcolor: hoverWorkflowStageId === String(column.id) ? '#e0f2fe' : 'transparent',
                transition: 'background-color 0.2s ease',
              }}
            >
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    fontWeight: 800,
                    color: stageTheme.columnHeader,
                    fontSize: '1rem',
                  }}
                >
                  {column.name}
                </Typography>
                {(workflowBoard[String(column.id)] || []).map((task) => (
                  <Paper
                    key={task.id}
                    variant="outlined"
                    draggable
                    onDragStart={(event) => onTaskDragStart(event, task.id)}
                    sx={{
                      p: 1.2,
                      mb: 1.5,
                      ml: 0.5,
                      mr: 0.5,
                      bgcolor: priorityTheme[task.priority]?.bg || stageTheme.cardBg,
                      borderRadius: 1.5,
                      borderColor: priorityTheme[task.priority]?.bg || stageTheme.cardBorder,
                      minHeight: 106,
                      transform: getStickyTransform((workflowBoard[String(column.id)] || []).findIndex((x) => x.id === task.id)),
                      transformOrigin: 'center',
                      cursor: 'grab',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.8, pr: 1 }}>
                        {task.title}
                      </Typography>
                      <IconButton size="small" onClick={(event) => openTaskMenu(event, task)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Box sx={{ mt: 0.8, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={(task.priority || '').toUpperCase()}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'rgba(255,255,255,0.6)',
                          color: priorityTheme[task.priority]?.color || '#374151',
                        }}
                      />
                      <Chip label={task.task_type_name || task.category || ''} size="small" variant="outlined" />
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 0.8 }}>
                      {task.assigned_to_name || 'Unassigned'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {task.sprint_name || 'No sprint'}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {tab === 'board' && !selectedWorkflowTypeId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a task type above to show workflow columns (Backlog and other stages from Task types & stages).
        </Alert>
      )}

      {tab === 'timeline' && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Task History Timeline
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                size="small"
                label="From date"
                value={timelineFrom}
                onChange={(e) => setTimelineFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                size="small"
                label="To date"
                value={timelineTo}
                onChange={(e) => setTimelineTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
          {renderTimelineLineChart()}
        </Paper>
      )}

      {tab === 'list' && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          {kanbanTasks.length === 0 && <Typography variant="body2" color="text.secondary">No tasks found.</Typography>}
          {kanbanTasks.map((task) => (
            <Stack key={`list-${task.id}`} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: '1px solid #e5e7eb' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>{task.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {task.category} | {task.priority} | {task.assigned_to_name || 'Unassigned'}
                </Typography>
              </Box>
              <Chip
                label={task.task_stage_name || task.task_stage_code || '—'}
                size="small"
              />
            </Stack>
          ))}
        </Paper>
      )}

      {rowCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 2 }}>
          <Pagination
            count={Math.ceil(rowCount / paginationModel.pageSize)}
            page={paginationModel.page + 1}
            onChange={(_, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage - 1 }))}
            color="primary"
          />
        </Box>
      )}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeTaskMenu}>
        <MenuItem onClick={openTaskDetails}>View Full Details</MenuItem>
      </Menu>

      <Dialog open={detailsDialog.open} onClose={closeTaskDetails} fullWidth maxWidth="md">
        <DialogTitle>Task Details</DialogTitle>
        <DialogContent dividers>
          {detailsDialog.loading && <Typography variant="body2">Loading...</Typography>}
          {!detailsDialog.loading && detailsDialog.task && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>{detailsDialog.task.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {detailsDialog.task.description || 'No description'}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body2"><strong>Stage:</strong> {detailsDialog.task.task_stage_name || detailsDialog.task.task_stage_code || '—'}</Typography>
                <Typography variant="body2"><strong>Priority:</strong> {detailsDialog.task.priority || 'NA'}</Typography>
                <Typography variant="body2"><strong>Task type:</strong> {detailsDialog.task.task_type_name || detailsDialog.task.category || 'NA'}</Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body2"><strong>Assignee:</strong> {detailsDialog.task.assigned_to_name || 'Unassigned'}</Typography>
                <Typography variant="body2"><strong>Sprint:</strong> {detailsDialog.task.sprint_name || 'No sprint'}</Typography>
                <Typography variant="body2"><strong>Deadline:</strong> {detailsDialog.task.deadline || 'NA'}</Typography>
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Comments</Typography>
              {((detailsDialog.task.details?.comments) || []).map((comment, idx) => (
                <Typography key={`comment-${idx}`} variant="body2" sx={{ mb: 0.8 }}>
                  - {comment.text}
                </Typography>
              ))}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add comment"
                  value={newComment}
                  onChange={(event) => setNewComment(event.target.value)}
                />
                <Button variant="contained" onClick={addComment}>Add Comment</Button>
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Subtasks</Typography>
              {((detailsDialog.task.details?.subtasks) || []).map((subtask, idx) => (
                <Typography key={`subtask-${idx}`} variant="body2" sx={{ mb: 0.8 }}>
                  - {subtask.title}
                </Typography>
              ))}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add subtask"
                  value={newSubtask}
                  onChange={(event) => setNewSubtask(event.target.value)}
                />
                <Button variant="contained" onClick={addSubtask}>Add Subtask</Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTaskDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TaskKanban;
